import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../utils/i18n';
import { bankAPI } from '../../../utils/api';
import { ChevronRight } from 'lucide-react';
import './StepStyles.css';

const Step2Bank = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [banksLoaded, setBanksLoaded] = useState(false);

  useEffect(() => {
    fetchBanks();
  }, []);

  // Load branches when bankId changes and banks are loaded
  useEffect(() => {
    if (banksLoaded && formData.bankId) {
      fetchBranches(formData.bankId);
    }
  }, [banksLoaded, formData.bankId]);

  useEffect(() => {
    // Check if returning from bank selection page (via sessionStorage)
    const selectedBankId = sessionStorage.getItem('selectedBankId');
    const selectedBranchId = sessionStorage.getItem('selectedBranchId');

    if (selectedBankId) {
      setFormData(prev => ({
        ...prev,
        bankId: parseInt(selectedBankId),
        branchId: selectedBranchId ? parseInt(selectedBranchId) : '',
      }));

      // Clear sessionStorage after applying
      sessionStorage.removeItem('selectedBankId');
      sessionStorage.removeItem('selectedBranchId');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-check when pathname changes (navigating back)

  const fetchBanks = async () => {
    try {
      const response = await bankAPI.getAll();
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
      // Fallback to default banks
      setBanks([
        { id: 1, name: '전북은행', fullName: 'Jeonbuk Bank (전북은행)', nameEn: 'Jeonbuk Bank' },
        { id: 2, name: '광주은행', fullName: 'Kwangju Bank (광주은행)', nameEn: 'Kwangju Bank' },
        { id: 3, name: '대부업', fullName: 'Lending Company (대부업)', nameEn: 'Lending Company' },
      ]);
    } finally {
      setBanksLoaded(true);
    }
  };

  const fetchBranches = async (bankId) => {
    try {
      const response = await bankAPI.getBranches(bankId);
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      setBranches([]);
    }
  };

  const handleOpenBankSelection = () => {
    navigate('/loan/select-bank', {
      state: {
        bankId: formData.bankId,
        branchId: formData.branchId,
        selectBranchOnly: false, // Select bank (and branch if available)
      }
    });
  };

  const handleOpenBranchSelection = () => {
    navigate('/loan/select-bank', {
      state: {
        bankId: formData.bankId,
        branchId: formData.branchId,
        selectBranchOnly: true, // Go directly to branch selection
      }
    });
  };

  const selectedBank = banks.find(b => b.id === formData.bankId);
  const selectedBranch = branches.find(b => b.id === formData.branchId);
  const hasBranches = branches.length > 0;

  return (
    <div className="step-container">
      <div className="form-section">
        {/* Bank Selection Button */}
        <div className="form-group">
          <label className="form-label">
            {t('bankLabel')} <span className="required">*</span>
          </label>
          <button
            type="button"
            className="selection-button"
            onClick={handleOpenBankSelection}
          >
            <div className="selection-content">
              {selectedBank ? (
                <>
                  <div className="selection-text">{selectedBank.name}</div>
                  {selectedBank.fullName && (
                    <div className="selection-desc">{selectedBank.fullName}</div>
                  )}
                </>
              ) : (
                <div className="selection-placeholder">{t('selectBank')}</div>
              )}
            </div>
            <ChevronRight size={20} className="selection-icon" />
          </button>
        </div>

        {/* Branch Selection Button (if bank has branches) */}
        {formData.bankId && hasBranches && (
          <div className="form-group">
            <label className="form-label">{t('branchLabel')}</label>
            <button
              type="button"
              className="selection-button"
              onClick={handleOpenBranchSelection}
            >
              <div className="selection-content">
                {selectedBranch ? (
                  <>
                    <div className="selection-text">{selectedBranch.name}</div>
                    {selectedBranch.address && (
                      <div className="selection-desc">{selectedBranch.address}</div>
                    )}
                  </>
                ) : (
                  <div className="selection-placeholder">{t('selectBranch')}</div>
                )}
              </div>
              <ChevronRight size={20} className="selection-icon" />
            </button>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {!formData.bankId && (
        <div className="step-info warning">
          ⚠️ {t('pleaseSelectBank')}
        </div>
      )}

      {formData.bankId && !hasBranches && (
        <div className="step-info success">
          ✓ {t('bankSelected')} - {t('noBranchesAvailable')}
        </div>
      )}

      {formData.bankId && hasBranches && formData.branchId && (
        <div className="step-info success">
          ✓ {t('bankAndBranchSelected')}
        </div>
      )}
    </div>
  );
};

export default Step2Bank;
